import React, { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { vendorGates } from '../core/adminPermissions'
import {
  bulkImportVendors,
  downloadVendorImportTemplate,
  getCompanyList,
  getVendorCompanyGroupOptions,
  previewVendorImport,
} from '../core/_requests'
import type { Company } from '../core/_models'
import type { VendorCompanyGroupOption } from '../core/_requests'
import type { VendorImportPreview, VendorImportResult } from '../core/_models'
import { buildVendorImportErrorReportCsv } from '../core/vendorImportHeaders'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'

const backdropStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.55)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1.25rem',
  overflowY: 'auto',
}

interface BulkVendorUploadModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

type UploadStep = 'file' | 'preview' | 'result'

const BulkVendorUploadModal: FC<BulkVendorUploadModalProps> = ({ visible, onClose, onSuccess }) => {
  const { currentUser, isSuperAdmin, hasPermission } = useAuth()
  const actionGates = useMemo(
    () => vendorGates(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  )

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [step, setStep] = useState<UploadStep>('file')
  const [preview, setPreview] = useState<VendorImportPreview | null>(null)
  const [result, setResult] = useState<VendorImportResult | null>(null)
  const [companyGroups, setCompanyGroups] = useState<VendorCompanyGroupOption[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<Company[]>([])
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('')

  const ownCompanyGroupId = useMemo(() => {
    return (currentUser as any)?.companyGroupAdminId ??
      (currentUser as any)?.companyGroupAdmin?.id ??
      (currentUser as any)?.cgaid?.id ??
      null
  }, [currentUser])

  const ownCompanyId = useMemo(() => {
    return (currentUser as any)?.companyAdminId ??
      (currentUser as any)?.companyAdmin?.id ??
      (currentUser as any)?.caid?.id ??
      null
  }, [currentUser])

  const visibleCompanies = useMemo(() => {
    if (!selectedCompanyGroupId) return companyAdmins
    return companyAdmins.filter((company) => Number(company?.cga?.id) === Number(selectedCompanyGroupId))
  }, [companyAdmins, selectedCompanyGroupId])

  const scopeParams = useMemo(() => ({
    companyGroupAdminId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : null,
    companyAdminId: selectedCompanyId ? Number(selectedCompanyId) : null,
  }), [selectedCompanyGroupId, selectedCompanyId])

  useEffect(() => {
    if (!visible) return
    if (!actionGates.canCreate) return
    void loadScopeOptions()
  }, [visible, actionGates.canCreate])

  useEffect(() => {
    if (!visible) {
      setSelectedFile(null)
      setPreview(null)
      setResult(null)
      setUploading(false)
      setPreviewing(false)
      setStep('file')
      if (!isSuperAdmin) {
        setSelectedCompanyGroupId('')
        setSelectedCompanyId('')
      }
    }
  }, [visible, isSuperAdmin])

  useEffect(() => {
    if (!isSuperAdmin) return
    if (selectedCompanyId && visibleCompanies.every((company) => Number(company.id) !== Number(selectedCompanyId))) {
      setSelectedCompanyId('')
    }
  }, [selectedCompanyGroupId, visibleCompanies, isSuperAdmin, selectedCompanyId])

  const loadScopeOptions = async () => {
    try {
      if (isSuperAdmin) {
        const [groups, companies] = await Promise.all([
          getVendorCompanyGroupOptions(),
          getCompanyList(),
        ])
        setCompanyGroups(groups)
        setCompanyAdmins(companies)
      } else {
        setSelectedCompanyGroupId((ownCompanyGroupId as number | null) ?? '')
        setSelectedCompanyId((ownCompanyId as number | null) ?? '')
      }
    } catch (error: any) {
      console.error('Failed to load upload scope options:', error)
      showErrorToast(error, 'Failed to load company options.')
    }
  }

  const validateScope = (): boolean => {
    if (isSuperAdmin && !selectedCompanyGroupId && !selectedCompanyId) {
      toast.error('Select a company group or company for this upload')
      return false
    }
    return true
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) return
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
    if (!['.xlsx', '.xls', '.csv'].includes(extension)) {
      toast.error('Please select a .xlsx, .xls, or .csv file')
      event.target.value = ''
      return
    }
    setSelectedFile(file)
    setPreview(null)
    setResult(null)
    setStep('file')
  }

  const handleDownloadTemplate = async (format: 'csv' | 'xlsx' = 'csv') => {
    try {
      const blob = await downloadVendorImportTemplate(format)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = format === 'xlsx' ? 'vendor-import-template.xlsx' : 'vendor-import-template.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Template downloaded successfully')
    } catch (error: any) {
      console.error('Failed to download template:', error)
      showErrorToast(error, 'Failed to download template.')
    }
  }

  const handlePreview = async () => {
    if (!actionGates.canCreate) {
      toast.error('You do not have permission to upload vendors')
      return
    }
    if (!selectedFile) {
      toast.error('Please choose a file first')
      return
    }
    if (!validateScope()) return

    setPreviewing(true)
    try {
      const response = await previewVendorImport(
        selectedFile,
        scopeParams.companyGroupAdminId,
        scopeParams.companyAdminId
      )
      setPreview(response)
      setStep('preview')
      if (response.ignoredOwnershipColumns?.length) {
        toast.info(
          `Ownership columns (${response.ignoredOwnershipColumns.join(', ')}) were ignored. Scope is derived from your account.`
        )
      }
    } catch (error: any) {
      console.error('Preview failed:', error)
      showErrorToast(error, 'Preview failed.')
    } finally {
      setPreviewing(false)
    }
  }

  const handleUpload = async () => {
    if (!actionGates.canCreate) {
      toast.error('You do not have permission to upload vendors')
      return
    }
    if (!selectedFile) {
      toast.error('Please choose a file first')
      return
    }
    if (!validateScope()) return
    if (preview && preview.errorCount > 0) {
      toast.warn('Invalid rows will be skipped. Only valid rows will be imported.')
    }

    setUploading(true)
    try {
      const response = await bulkImportVendors(
        selectedFile,
        scopeParams.companyGroupAdminId,
        scopeParams.companyAdminId
      )
      setResult(response)
      setStep('result')
      toast.success('Vendor bulk upload completed')
      onSuccess?.()
    } catch (error: any) {
      console.error('Bulk upload failed:', error)
      showErrorToast(error, 'Bulk upload failed.', 'create')
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadErrorReport = () => {
    if (!result?.errors?.length) return
    const csv = buildVendorImportErrorReportCsv(result.errors, preview?.rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'vendor-import-errors.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const flattenResultErrors = () => {
    if (!result?.errors?.length) return []
    return result.errors.flatMap((error) => {
      if (error.errors?.length) {
        return error.errors.map((fieldError) => ({
          rowNumber: error.rowNumber,
          field: fieldError.field,
          value: fieldError.value,
          reason: fieldError.message ?? error.reason ?? error.message,
        }))
      }
      return [{
        rowNumber: error.rowNumber,
        field: error.field,
        value: error.value,
        reason: error.message ?? error.reason ?? 'Unknown error',
      }]
    })
  }

  if (!visible) return null

  return (
    <div style={backdropStyle}>
      <div
        className='card shadow-lg w-100 bg-white'
        style={{
          maxWidth: '960px',
          maxHeight: 'calc(100vh - 5rem)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
          overflow: 'hidden',
          margin: 'auto',
        }}
      >
        <div
          className='card-header d-flex align-items-center justify-content-between px-6 py-4 border-bottom'
          style={{ flexShrink: 0, minHeight: 'unset' }}
        >
          <div>
            <h3 className='mb-1 fw-bold text-dark'>Bulk Vendor Upload</h3>
            <div className='text-muted fs-7'>Preview validation before importing vendors from CSV or Excel.</div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            style={{ borderRadius: '8px' }}
            onClick={onClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
          </button>
        </div>

        <div className='card-body px-6 py-5' style={{ overflowY: 'auto', flexGrow: 1 }}>
          {!actionGates.canCreate && (
            <div className='alert alert-danger mb-4'>You do not have permission to bulk upload vendors.</div>
          )}

          {isSuperAdmin && (
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>Target Company Group</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCompanyGroupId}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : ''
                    setSelectedCompanyGroupId(value)
                    setSelectedCompanyId('')
                  }}
                >
                  <option value=''>Select company group</option>
                  {companyGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name || `Group #${group.id}`}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
              <div className='col-md-6'>
                <label className='form-label fw-semibold'>Target Company / Sub Company</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value=''>Select company (optional)</option>
                  {visibleCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name || `Company #${company.id}`}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>
          )}

          {!isSuperAdmin && (
            <div className='alert alert-info mb-4'>
              Upload scope is derived from your account. The template does not include Company Group or Company columns.
            </div>
          )}

          <div className='d-flex gap-2 flex-wrap mb-3'>
            <button type='button' className='btn btn-light-primary' onClick={() => handleDownloadTemplate('csv')}>
              Download CSV Template
            </button>
            <button type='button' className='btn btn-light' onClick={() => handleDownloadTemplate('xlsx')}>
              Download XLSX Template
            </button>
            <label className='btn btn-outline-primary mb-0'>
              Choose File
              <input
                type='file'
                className='d-none'
                accept='.xlsx,.xls,.csv'
                onChange={handleFileChange}
              />
            </label>
          </div>

          <div className='mb-4'>
            <div className='border rounded p-3 bg-light'>
              <div className='fw-semibold mb-1'>Selected file</div>
              <div className='text-muted'>{selectedFile ? selectedFile.name : 'No file selected'}</div>
            </div>
          </div>

          {step === 'preview' && preview && (
            <div className='border rounded p-3 mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Import Preview</h5>
                <div className='text-muted small'>
                  {preview.validCount} ready · {preview.errorCount} with errors · {preview.totalRows} total rows
                </div>
              </div>
              <div className='table-responsive' style={{ maxHeight: '320px', overflowY: 'auto' }}>
                <table className='table table-sm table-bordered align-middle'>
                  <thead className='table-light'>
                    <tr>
                      <th>Row</th>
                      <th>Vendor Name</th>
                      <th>Vendor Code</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Categories</th>
                      <th>Service Regions</th>
                      <th>Status</th>
                      <th>Validation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.rowNumber}>
                        <td>{row.rowNumber}</td>
                        <td>{row.vendorName || '—'}</td>
                        <td>{row.vendorCode || '—'}</td>
                        <td>{row.email || '—'}</td>
                        <td>
                          <span style={{ fontFamily: 'monospace' }} title={row.password || 'Auto-generated default password'}>
                            {row.password ? '••••••••' : <span className='text-muted small'>Auto-generated</span>}
                          </span>
                        </td>
                        <td>{row.categories || '—'}</td>
                        <td>{row.serviceRegions || '—'}</td>
                        <td>
                          <span className={`badge ${row.status === 'Ready' ? 'badge-light-success' : 'badge-light-danger'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          {row.errors?.length
                            ? row.errors.map((err, idx) => (
                                <div key={`${row.rowNumber}-${idx}`} className='text-danger small'>
                                  {err.message}
                                </div>
                              ))
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errorCount > 0 && (
                <div className='alert alert-warning mt-3 mb-0'>
                  {preview.errorCount} row(s) have validation errors and will be skipped during import.
                </div>
              )}
            </div>
          )}

          <div className='d-flex justify-content-end gap-2 mb-4'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={uploading || previewing}>
              Close
            </button>
            {step !== 'result' && (
              <button
                type='button'
                className='btn btn-light-primary d-flex align-items-center gap-2'
                onClick={handlePreview}
                disabled={previewing || uploading || !selectedFile || !actionGates.canCreate}
              >
                {previewing && <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />}
                {previewing ? 'Previewing...' : 'Preview Import'}
              </button>
            )}
            <button
              type='button'
              className='btn btn-primary d-flex align-items-center gap-2'
              onClick={handleUpload}
              disabled={uploading || previewing || !selectedFile || !actionGates.canCreate}
            >
              {uploading && <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />}
              {uploading ? 'Uploading...' : 'Upload Vendors'}
            </button>
          </div>

          {step === 'result' && result && (
            <div className='border-top pt-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Upload Result</h5>
                {result.errors?.length > 0 && (
                  <button type='button' className='btn btn-sm btn-light-primary' onClick={handleDownloadErrorReport}>
                    Download Error Report
                  </button>
                )}
              </div>
              <div className='row g-3 mb-3'>
                <div className='col-6 col-md-2'><div className='border rounded p-3 text-center'><div className='fw-bold'>{result.totalRows}</div><div className='text-muted small'>Rows</div></div></div>
                <div className='col-6 col-md-2'><div className='border rounded p-3 text-center'><div className='fw-bold text-success'>{result.createdCount}</div><div className='text-muted small'>Created</div></div></div>
                <div className='col-6 col-md-2'><div className='border rounded p-3 text-center'><div className='fw-bold text-primary'>{result.updatedCount}</div><div className='text-muted small'>Updated</div></div></div>
                <div className='col-6 col-md-2'><div className='border rounded p-3 text-center'><div className='fw-bold text-warning'>{result.skippedCount}</div><div className='text-muted small'>Skipped</div></div></div>
                <div className='col-6 col-md-2'><div className='border rounded p-3 text-center'><div className='fw-bold text-danger'>{result.failedCount}</div><div className='text-muted small'>Failed</div></div></div>
              </div>

              {flattenResultErrors().length > 0 && (
                <div className='table-responsive'>
                  <table className='table table-sm table-bordered align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th style={{ width: '80px' }}>Row</th>
                        <th style={{ width: '140px' }}>Field</th>
                        <th style={{ width: '180px' }}>Value</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flattenResultErrors().map((error, index) => (
                        <tr key={`${error.rowNumber}-${error.field}-${index}`}>
                          <td>{error.rowNumber}</td>
                          <td>{error.field || '—'}</td>
                          <td>{error.value || '—'}</td>
                          <td>{error.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkVendorUploadModal
